<script>
  import { format } from 'svelte-i18n';
  import { Offcanvas } from '@sveltestrap/sveltestrap';
  import { format as formatDate } from 'date-fns';

  import { generatePitchChartDataset, PitchChart } from '@client/components/charts/pitch';
  import { Icon } from '@client/components/icon';
  import { Recipient } from '@client/components/recipient';
  import { RecipientFiltering } from '@client/components/recipient-filtering';

  import { PitchService } from '@client/services/pitch';

  import { appStore } from '@client/stores/app';

  import { getFlag } from '@client/utils/flags';
  import { buildPitchReport } from '@client/utils/pitch';

  import { stringIndexArraySort } from '@common/utils/arrays';
  import { durationFromSeconds } from '@common/utils/dates';
  import { formatBytes } from '@common/utils/numbers';

  export let row = {};
  export let recipientFilter = null;

  let fusionUrl = '';
  let activeFusionReplay = '';
  let activeFusionReplayConfig = {};

  const changeFusionReplay = (url, config) => {
    if (activeFusionReplay === url) {
      activeFusionReplay = '';
      fusionUrl = '';
      activeFusionReplayConfig = {};
      return;
    }

    activeFusionReplayConfig = config;
    fusionUrl = url;
    activeFusionReplay = fusionUrl;
  };

  const onIframeLoad = function () {
    this.style.visibility = 'visible';
    this.style.height = (this.contentWindow.document.body.firstChild.clientHeight + 20) / 2 + 'px';
    this.style.width = (this.contentWindow.document.body.firstChild.clientWidth + 20) / 2 + 'px';
  };

  const generatePitchRecipientSessions = (sessions, filename) => {
    const ids = Object.keys(sessions);
    const grouped = Object.values(sessions).reduce((acc, config, index) => {
      const sessionId = ids[index].replace('-id', '');

      if (config.details === filename) {
        if (!acc[config.recipient]) {
          acc[config.recipient] = [];
        }

        if (config.series.filter(Boolean).length) {
          acc[config.recipient].push({
            ...config,
            duration: durationFromSeconds(config.series.reduce((sum, value) => sum + value, 0) / 1000),
            slidesviewed: config.series.filter(Boolean).length,
            sessionId,
            replayUrl: `https://www.sendergen.com/docsession/${config.msghashid}/${sessionId}/watch.html`
          });
        }
      }

      return acc;
    }, {});

    const sorted = Object.keys(grouped)
      .sort()
      .reduce((acc, key) => {
        acc[key] = grouped[key].sort((a, b) => new Date(b.utcts) - new Date(a.utcts));
        return acc;
      }, {});

    return Object.entries(sorted);
  };

  const groupByDates = (data, field, atts) => {
    const grouped = data.reduce((acc, config) => {
      const date = formatDate(new Date(config[field]), 'M/d/yyyy');
      if (!acc[date]) {
        acc[date] = [];
      }

      acc[date].push(config);

      return acc;
    }, {});

    return Object.keys(grouped).reduce((acc, key) => {
      acc[key] = grouped[key].sort((a, b) => new Date(b.utcts) - new Date(a.utcts));
      return acc;
    }, {});
  };

  let chartData = {};

  const getChartData = (config) => {
    const datasets = generatePitchChartDataset(config.series).map((series) => {
      const findIndex = config.recipsarray.indexOf(recipientFilter);
      const recips = recipientFilter ? [config.recipsarray[findIndex]] : config.recipsarray;
      const data = recipientFilter
        ? series.data.map((value, index) => (recipientFilter && index !== findIndex ? 0 : value)).filter(Boolean)
        : series.data;

      if (recipientFilter) {
        config.recipsarray = recips;
      }

      series.data = stringIndexArraySort(recips, data);
      series.datasets = series.datasets.map((item) => stringIndexArraySort(recips, item.datasets));

      return series;
    });

    chartData[config.filename] = {
      config,
      datasets
    };

    return {
      animation: {
        duration: 10000
      },
      hash: config.hash,
      labels: config.recipsarray.sort(),
      datasets
    };
  };
</script>

{#key recipientFilter}
  <RecipientFiltering bind:recipient={recipientFilter}>
    {@html $format('label.TRACKING_RECIPIENT_FILTERING', { values: { filter: recipientFilter } })}
  </RecipientFiltering>
{/key}
<div class="fusion-layout">
  <div class="sessions">
    {#await PitchService.getAll({ msglink: row.msglink })}
      <div class="empty">{$format('label.LOADING_REPLAY_HISTORY')}</div>
    {:then response}
      {#each Object.entries(buildPitchReport(response)) as [id, config]}
        {#each Object.entries(config.atts) as [attsId, attsConfig]}
          <div class="pitch-chart">
            <div class="pitch-chart-title">
              <Icon name="paperclip" />
              <span>{attsConfig.filename} - {formatBytes(attsConfig.filesize)}</span>
            </div>
            {#key recipientFilter}
              <PitchChart data={getChartData(attsConfig)} />
            {/key}
          </div>
          <div class="sessions">
            {#each Object.entries(config.sessions || {}) as [sessionId, sessionInfo]}
              {#each generatePitchRecipientSessions(config.sessions[sessionId], attsConfig.filename) as [recipient, records]}
                {#if recipientFilter === recipient || !recipientFilter}
                  {@const groupedDates = Object.entries(groupByDates(records, 'utcts', attsConfig))}
                  <div class="recipient-history">
                    <Recipient {recipient} history={[]} />
                    {#if !groupedDates.length}
                      <div class="empty">{$format('label.NO_REPLAY_HISTORY_AVAVLIABLE')}</div>
                    {:else}
                      {#each groupedDates as [date, records]}
                        <div class="day-block">
                          <div class="date">{formatDate(new Date(date), 'EEEE, MMM d, yyyy')}</div>
                          <div class="timeline">
                            {#each records as record}
                              <div
                                class="item"
                                role="button"
                                tabindex="0"
                                on:click={() => changeFusionReplay(record.replayUrl, record)}
                                class:active={activeFusionReplay === record.replayUrl}
                              >
                                <div class="icon">
                                  <i
                                    class:os-icon-play-circle={activeFusionReplay !== record.replayUrl}
                                    class:os-icon-pause-circle={activeFusionReplay === record.replayUrl}
                                  />
                                </div>
                                <div class="time">
                                  <div>{formatDate(new Date(record.utcts), 'h:mm:ss aa')}</div>
                                  <small
                                    >{record.duration} - {record.slidesviewed}
                                    {record.slidesviewed > 1
                                      ? $format('label.PAGES').toLowerCase()
                                      : $format('label.PAGE').toLowerCase()}</small
                                  >
                                </div>
                                <div class="details">
                                  <div>
                                    <span class={getFlag(record.location)} />
                                    {record.location}
                                  </div>
                                  <small>
                                    {$format('label.ISP_IP', { values: { isp: record.isp, ip: record.ip } })}
                                  </small>
                                  <a href={record.replayUrl} target="_blank" class="external-link">
                                    <Icon name="external-link" />
                                  </a>
                                </div>
                              </div>
                            {/each}
                          </div>
                        </div>
                      {/each}
                    {/if}
                  </div>
                {/if}
              {/each}
            {/each}
          </div>
        {/each}
      {/each}
    {:catch}
      <div class="empty">{$format('label.NO_REPLAY_HISTORY_AVAVLIABLE')}</div>
    {/await}
  </div>

  {#if $appStore.mobile}
    <Offcanvas
      classs="activity-offcanvas"
      isOpen={activeFusionReplay}
      toggle={() => changeFusionReplay()}
      placement="end"
      style="width: 98%"
    >
      <div slot="header" class="offcanvas-heading">
        <Icon name="video" />
        {#if activeFusionReplay}
          <span>{formatDate(new Date(activeFusionReplayConfig.utcts), 'EEEE, MMM d, yyyy @ h:mm:ss aa')}</span>
        {/if}
      </div>
      <div class="offcanvas-playback">
        {#if activeFusionReplay}
          <div class="details">
            <div>
              <span class={getFlag(activeFusionReplayConfig.note.location)} />
              {activeFusionReplayConfig.note.location}
            </div>
            <small>
              {$format('label.ISP_IP', {
                values: { isp: activeFusionReplayConfig.note.isp, ip: activeFusionReplayConfig.ip }
              })}
            </small>
          </div>
        {/if}
        {#if fusionUrl}
          {#key fusionUrl}
            <iframe tabindex="-1" style="visibility: hidden;" title="preview" on:load={onIframeLoad} src={fusionUrl} />
          {/key}
        {:else}
          <div class="empty-player">{$format('label.SLIDE_ANALYTICS_SELECT_SESSION')}</div>
        {/if}
      </div>
    </Offcanvas>
  {:else}
    <div class="iframe">
      {#if fusionUrl}
        {#key fusionUrl}
          <iframe tabindex="-1" style="visibility: hidden;" title="preview" on:load={onIframeLoad} src={fusionUrl} />
        {/key}
      {:else}
        <div class="empty-player">{$format('label.SLIDE_ANALYTICS_SELECT_SESSION')}</div>
      {/if}
    </div>
  {/if}
</div>

<style lang="scss" src="./attachments.scss"></style>
